<template>
  <div class="gs-app gm-automations-v2-refresh">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="flex h-full automations-wrapper">
      <div class="w-full py-4 gm-automations-v2-refresh__workspace">

          <!-- v-if="!(!selectedFilter && this.automations.length === 0)" -->
        <div class="flex mb-2 p-2 gs-refresh-controls" v-if="automations.length">
          <div class="w-full flex justify-end items-center">
            <div class="mr-2">
              <p>{{ $t('frontend_sort_by') }}:</p>
            </div>
            <div class="w-48 mr-4 flex">
              <gd-dropdown
                class="h-10 custom-treeselect"
                :placeholder="$t('frontend_select')"
                v-model="currentSortOption"
                :options="automationSortOptions"
                @change="sortOptionChanged($event)"
              />
            </div>
            <gd-button
              variant="primary"
              class="w-56 automation-add-btn"
              @click="openAutomationCreateModal"
            >
              {{$t('frontend_groovemail_analytics_list_new_automation')}}
            </gd-button>
          </div>
        </div>
        <div class="flex mb-4">
          <div class="w-full p-2">
            <div v-if="automations && automations.length">
              <div
                v-for="(automation, index) in automations"
                :key="index"
                class="
                  w-full
                  bg-white
                  border-t border-l border-r
                  rounded-t rounded-r
                  border-grey-300
                  automation-container
                  gm-automations-v2-refresh__card
                "
                :class="{
                  'border-b rounded-l': index === automations.length - 1,
                }"
              >
                <div class="flex justify-between pt-6 px-6 pb-2">
                  <div class="flex items-center">
                    <div>
                      <p
                        class="font-semibold text-md text-deep-dark text-2xl"
                      >
                        {{
                          automation.name
                            ? automation.name
                            : this.$t('frontend_groovemail_not_available')
                        }}
                      </p>
                    </div>
                    <div
                      class="cursor-pointer ml-4"
                      @click="
                        toggleDetailedAutomationView(
                          index,
                          automation.showDetailedView
                        )
                      "
                      :class="{
                        'af-caret-rotate-0': automation.showDetailedView,
                        'af-caret-rotate-180': !automation.showDetailedView,
                      }"
                    >
                      <i
                        class="text-grey-500 fa fa-caret-up"
                        aria-hidden="true"
                      ></i>
                    </div>
                  </div>
                  <div class="flex justify-center items-center">
                    <div class="mr-2 text-sm text-grey-700">
                      <span class="italic">
                        {{ $t('frontend_groovemail_automations_status_automation_is_in_label')}}
                      </span>
                      <span
                        class="font-bold"
                        :class="{
                          'text-green-extra-dark': automation.status === 1,
                          'text-danger': !automation.status,
                        }"
                        >{{
                          automation.status === 1
                            ? $t(
                                'frontend_groovemail_sequence_node_status_active'
                              )
                            : $t(
                                'frontend_groovemail_sequence_node_status_inactive'
                              )
                        }}</span
                      >
                      <span class="italic">
                        {{$t('frontend_groovemail_automations_status_state_label')}}
                      </span>
                    </div>

                    <gd-dropdown
                      class="ml-2 font-semibold"
                      size="regular"
                      :placeholder="$t('frontend_groovemail_mail_lead_actions')"
                      :options="getActionOptions(automation)"
                      @change="handleActionChange($event, index, automation)"
                      divider
                    />

                  </div>
                </div>
                <div
                  class="flex"
                  :class="{ 'pb-4': !automation.showDetailedView }"
                >
                  <div
                    class="
                      automation-section-trigger
                      flex
                      py-2
                      px-6
                      items-center
                    "
                  >
                    <TriggersIcon class="mr-2" />
                    <p class="text-deep-dark break-all text-sm font-bold">
                      {{ $t('frontend_groovemail_sequence_analytics_triggers') }}
                      <span v-if="!automation.showDetailedView">(1)</span>
                    </p>
                  </div>

                  <div class="w-16 px-4 pt-4"></div>

                  <div
                    class="
                      automation-section-trigger
                      flex
                      py-2
                      px-6
                      items-center
                    "
                    v-if="automation.pre_condition.length > 1"
                  >
                    <ConditionsIcon class="mr-2" />
                    <p class="text-deep-dark break-all text-sm font-bold">
                      {{ $t('frontend_conditions') }}
                      <span v-if="!automation.showDetailedView"
                        >({{
                          automation.pre_condition &&
                          automation.pre_condition.length
                            ? automation.pre_condition.length - 1
                            : 0
                        }})</span
                      >
                    </p>
                  </div>

                  <div
                    class="w-16 px-4 pt-4"
                    v-if="automation.pre_condition.length > 1"
                  ></div>

                  <div
                    class="
                      automation-section-trigger
                      flex
                      py-2
                      px-6
                      items-center
                    "
                  >
                    <ActionsIcon class="mr-2" />
                    <p class="text-deep-dark break-all text-sm font-bold">
                      {{ $t('frontend_actions') }}
                      <span v-if="!automation.showDetailedView"
                        >({{
                          automation.post_condition &&
                          automation.post_condition.length
                            ? automation.post_condition.length
                            : 0
                        }})</span
                      >
                    </p>
                  </div>
                </div>
                <transition name="bounce">
                  <div v-if="automation.showDetailedView">
                    <div class="flex pt-2 pb-2 px-6">
                      <div class="automation-section-trigger">
                        <div v-for="(option, index) in automation.pre_condition" :key="index" class="flex mb-4">
                          <template v-if="index === 0">
                            <div>
                              <component
                                :is="getAutomationFilterNodeIcon(option)"
                              ></component>
                            </div>
                            <div class="w-10/12 ml-4">
                              <p
                                :title="option.activity_label"
                                class="
                                  overflow-hidden
                                  automate-text-ellipse
                                  text-grey-600
                                  font-bold
                                  text-sm
                                "
                              >
                                {{ option.activity_label }}
                              </p>
                              <p
                                :title="getFilterName(option.config_name)"
                                class="
                                  overflow-hidden
                                  automate-text-ellipse
                                  text-deep-dark
                                  font-bold
                                  text-sm
                                "
                              >
                                {{ getFilterName(option.config_name) }}
                              </p>
                            </div>
                          </template>
                        </div>
                      </div>
                      <div
                        class="w-16 px-4 pt-4"
                        v-if="automation.pre_condition.length > 1"
                      >
                        <div
                          class="border border-grey-600 border-dashed"
                        ></div>
                      </div>
                      <div
                        class="automation-section-condition"
                        v-if="automation.pre_condition.length > 1"
                      >
                        <div v-for="(option, index) in automation.pre_condition" :key="index" class="flex mb-4">
                          <template v-if="index != 0">
                            <div>
                              <component
                                :is="getAutomationFilterNodeIcon(option)"
                              ></component>
                            </div>
                            <div class="w-10/12 ml-4">
                              <p
                                :title="option.activity_label"
                                class="
                                  overflow-hidden
                                  automate-text-ellipse
                                  text-grey-600
                                  font-bold
                                  text-sm
                                "
                              >
                                {{ option.activity_label }}
                              </p>
                              <p
                                :title="getFilterName(option.config_name)"
                                class="
                                  overflow-hidden
                                  automate-text-ellipse
                                  text-deep-dark
                                  font-bold
                                  text-sm
                                "
                              >
                                {{ getFilterName(option.config_name) }}
                              </p>
                            </div>
                          </template>
                        </div>
                      </div>
                      <div class="w-16 px-4 pt-4">
                        <div
                          class="border border-grey-600 border-dashed"
                        ></div>
                      </div>
                      <div class="automation-section-action">
                        <div v-for="(option, index) in automation.post_condition" :key="index" class="flex mb-4">
                          <div>
                            <component
                              :is="getAutomationFilterNodeIcon(option)"
                            ></component>
                          </div>
                          <div class="w-10/12 ml-4">
                            <p
                              :title="option.activity_label"
                              class="
                                overflow-hidden
                                automate-text-ellipse
                                text-grey-600
                                font-bold
                                text-sm
                              "
                            >
                              {{ option.activity_label }}
                            </p>
                            <p
                              :title="getFilterName(option.config_name)"
                              class="
                                overflow-hidden
                                automate-text-ellipse
                                text-deep-dark
                                font-bold
                                text-sm
                              "
                            >
                              {{ getFilterName(option.config_name) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
              <div class="flex justify-between">
                <div class="custom-select-dropdown mt-6">
                  <select
                    class="w-20"
                    v-model="listingOptions.limit"
                    :value="listingOptions.limit"
                    @change="pageSizeChanged()"
                  >
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="75">75</option>
                    <option :value="100">100</option>
                  </select>
                </div>
                <pagination
                  class="groovemail-common-paginate"
                  v-model="listingOptions.page"
                  :records="automationCount"
                  :options="options"
                  :per-page="listingOptions.limit"
                  @paginate="pageChanged"
                />
              </div>
            </div>
            <!-- v-if="
                !selectedFilter && this.automations.length === 0 && !isLoading
              " -->
            <div
              v-if="!(automations && automations.length)"
              class="flex justify-center"
            >
              <empty-listing-state
                :empty_state_heading="'frontend_groovemail_automations_route_page_title'"
                :empty_state_sub_heading="'frontend_groovemail_automations_no_automation'"
                :empty_state_button_text="'frontend_groovemail_analytics_list_new_automation'"
                @executeDataCreation="openAutomationCreateModal"
              ></empty-listing-state>
            </div>
            <!-- <div
              v-if="
                selectedFilter && this.automations.length === 0 && !isLoading
              "
              class="flex justify-center"
            >
              <empty-listing-state
                :empty_state_sub_heading="'frontend_dashboard_automations_no_automation_found'"
              ></empty-listing-state>
            </div> -->
          </div>
        </div>
      </div>
    </div>

    <gs-named-modal class="gs-refresh-controls"
      id="automation-clone-modal"
      name="automation-clone-modal"
      :title="$t('frontend_groovemail_automations_clone_action')"
    >
      <div class="p-6">
        <div class="flex flex-col">
          <label class="font-semibold text-sm text-grey-700 mb-2">{{
            $t('frontend_groovemail_analytics_list_automation_name')
          }}</label>
          <text-input
            variant="base"
            v-model="model.name"
            :placeholder="$t('frontend_groovemail_automations_enter_automation_name_label')"
            :validator="$v.model.name"
          />
        </div>

        <div class="flex justify-end mt-6">
          <gd-button
            size="auto"
            @click="closeAutomationNameModal"
          >
            {{ $t('frontend_cancel') }}
          </gd-button>
          <gd-button
            variant="primary"
            class="ml-2"
            size="auto"
            @click="createDuplicateAutomation"
          >
            {{ $t('frontend_clone') }}
          </gd-button>
        </div>
      </div>
    </gs-named-modal>

     <!-- confirm modal for automation delete & change statue -->
     <confirm-modal
      :ref="`confirmation-automation-action`"
      :name="`confirmation-automation-action`"
      :buttonText="confirmMessageBtnText"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="confirmMessage"
      @confirm="confirmAutomationAction"
      @closed="closeAutomationActionModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Pagination from 'vue-pagination-2';
import Treeselect from '@riophae/vue-treeselect';
import themeConfig from '@/theme/themeConfig.js';
import EmptyListingState from '@/components/empty-listing-state.vue';
import showAlert from '@/components/sweet-alert-popup.js';
import logsIconsMixin from '@/pages/mail_lead/logs-icons-mixin';

import '@riophae/vue-treeselect/dist/vue-treeselect.css';

// import icons
import TriggersIcon from '@/assets/images/icons/TriggersIcon.svg';
import ConditionsIcon from '@/assets/images/icons/ConditionsIcon.svg';
import ActionsIcon from '@/assets/images/icons/ActionsIcon.svg';
import DefaultActivityIcon from '@/assets/images/icons/default-activity-icon.svg';

export default {
  name: 'automation-listing',
  middleware: 'auth',
  mixins: [logsIconsMixin],

  data() {
    return {
      isLoading: false,
      fullPageLoader: true,
      automations: [],
      selectedOption: '',
      listingOptions: {
        page: 1,
        limit: 25,
        orderBy: '',
        sortBy: '',
      },
      options: {
        chunk: 25,
      },
      automationCount: 0,
      model: {
        name: '',
        automationTriggers: [],
        automationActions: [],
        status: 1,
      },
      currentSortOption: '',
      clearable: false,
      closeOnSelect: true,
      clearOnSelect: true,

      automationTarget: '',
      confirmTarget: '',
      confirmMessage: '',
      confirmMessageBtnText: '',
    };
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  async created() {
    this.listingOptions.page = 1;
    this.listingOptions.limit = 25;
    await this.fetchAutomations();
  },

  computed: {
    automationSortOptions() {
      return [
        {
          value: 'name_asc',
          text: this.$t('frontend_groovemail_automations_ui_update_sort_option_name_asc'),
          orderBy: 'name', sortBy: 'asc'
        },
        {
          value: 'name_desc',
          text: this.$t('frontend_groovemail_automations_ui_update_sort_option_name_desc'),
          orderBy: 'name', sortBy: 'desc'
        },
        {
          value: 'newest_desc',
          text: this.$t('frontend_groovemail_automations_ui_update_sort_option_newest_desc'),
          orderBy: 'created_at', sortBy: 'desc'
        },
        {
          value: 'old_asc',
          text: this.$t('frontend_groovemail_automations_ui_update_sort_option_oldest_asc'),
          orderBy: 'created_at', sortBy: 'asc'
        },
        {
          value: 'last_updated_desc',
          text: this.$t('frontend_groovemail_automations_ui_update_sort_option_last_updated_desc'),
          orderBy: 'updated_at', sortBy: 'desc'
        },
      ];
    },
  },

  methods: {
    // show error
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide error
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    getFilterName(filterOptionNames) {
      let filterOptionsValueName = '';
      const filterOptionsArray = Object.keys(filterOptionNames);
      for (
        let filterIn = 0;
        filterIn <= filterOptionsArray.length - 1;
        filterIn += 1
      ) {
        if (filterIn === filterOptionsArray.length - 1) {
          filterOptionsValueName += `${filterOptionNames[filterOptionsArray[filterIn]]}`;
        } else {
          filterOptionsValueName += `${filterOptionNames[filterOptionsArray[filterIn]]}, `;
        }
      }
      return filterOptionsValueName;
    },

    // get the action options for the automation dropdown
    getActionOptions(automation) {
      const options = [
        { 
          value: 'edit',
          showValue: this.$t('frontend_groovemail_mail_lead_actions'),
          text: this.$t('frontend_edit')
        },
        { 
          value: 'clone',
          showValue: this.$t('frontend_groovemail_mail_lead_actions'),
          text: this.$t('frontend_clone')
        },
        { 
          value: 'remove',
          showValue: this.$t('frontend_groovemail_mail_lead_actions'),
          text: this.$t('frontend_remove')
        },
      ];

      if (automation.status === 1) {
        options.unshift({
          value: 'stop',
          showValue: this.$t('frontend_groovemail_mail_lead_actions'),
          text: this.$t('frontend_groovemail_automations_status_action_stop')
        });
      }

      if (automation.status === 0 || !automation.status) {
        options.unshift({
          value: 'start',
          showValue: this.$t('frontend_groovemail_mail_lead_actions'),
          text: this.$t('frontend_groovemail_automations_status_action_start')
        });
      }

      return options;
    },

    // handle the action change for the automation dropdown
    handleActionChange(selectedOption, index, automation) {
      switch (selectedOption) {
        case 'edit':
          this.editAutomation(automation.id);
          break;
        case 'stop':
          this.automationActionModal('statusChange', automation);
          break;
        case 'start':
          this.automationActionModal('statusChange', automation);
          break;
        case 'clone':
          this.cloneAutomation(automation.id);
          break;
        case 'remove':
          this.automationActionModal('delete', automation);
          break;

        default:
      }
    },

    setAutomationData(name, triggers, actions, status) {
      this.model.name = name || '';
      this.model.automationTriggers =
        triggers && triggers.length ? triggers : [];
      this.model.automationActions = actions && actions.length ? actions : [];
      this.model.status = status || 0;
    },

    async getAutomationListing() {
      this.showLoader();
      try {
        const automationListingUrl = this.getAutomationListingUrl();
        const automationsData = await axios.get(automationListingUrl);
        this.automations =
          automationsData && automationsData.data && automationsData.data.data
            ? automationsData.data.data
            : [];
        this.modifyAutomationsData();
        this.automationCount = automationsData.data.total;
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async fetchAutomations() {
      // show loader
      this.showLoader();
      try {
        const automationListingUrl = this.getAutomationListingUrl();
        const automationsData = await axios.get(automationListingUrl);
        this.automations =
          automationsData && automationsData.data && automationsData.data.data
            ? automationsData.data.data
            : [];
        this.modifyAutomationsData();
        this.automationCount = automationsData.data.total;
        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    getAutomationListingUrl() {
      let requestUrl = '/automation_v2/list?';
      Object.keys(this.listingOptions).forEach((optionKey, index) => {
        if (index === Object.keys(this.listingOptions).length - 1) {
          if (this.listingOptions[optionKey]) {
            requestUrl += `${optionKey}=${this.listingOptions[optionKey]}`;
          }
        } else if (this.listingOptions[optionKey]) {
          requestUrl += `${optionKey}=${this.listingOptions[optionKey]}&`;
        }
      });
      return requestUrl;
    },

    // open delete category modal
    async automationActionModal(option, automation) {
      // set the confirm target
      this.automationTarget = automation;
      
      // set the target option
      this.confirmTarget = option;

      // check the option and set the confirm message
      if (option === 'delete') {
        this.confirmMessage = this.$t('frontend_groovemail_swal_revert_message');
        this.confirmMessageBtnText = this.$t('frontend_delete');
      } else if (option === 'statusChange') {
        this.confirmMessage = !automation.status
          ? this.$t('frontend_groovemail_automations_are_you_sure_to_resume')
          : this.$t('frontend_groovemail_automations_are_you_sure_to_pause');
        this.confirmMessageBtnText = this.$t('frontend_confirm');
      }
      
      // open the modal
      this.$refs[`confirmation-automation-action`].open();
    },

    // close delete category modal
    closeAutomationActionModal() {
      // close the modal
      this.$refs[`confirmation-automation-action`].close();

      // reset the confirm target
      this.confirmTarget = '';
      this.confirmMessage = '';
      this.confirmMessageBtnText = '';
      this.automationId = '';
    },


    // confirm the delete category action
    confirmAutomationAction () {
      if(this.confirmTarget === 'delete') {
        this.deleteAutomtion();
      } else if (this.confirmTarget === 'statusChange') {
        this.changeAutomationStatus();
      }
    },

    async changeAutomationStatus() {
      this.showLoader();
      try {
        await axios.put(`automation_v2/${this.automationTarget.id}`, {
          status: 1 - this.automationTarget.status,
        });
        await this.getAutomationListing();
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async deleteAutomtion() {
      this.showLoader();
      try {
        await axios.delete(`automation_v2/${this.automationTarget.id}/delete`);
        await this.getAutomationListing();
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async editAutomation(automationId) {
      this.$router.push({
        name: 'AutomationEdit',
        params: { id: automationId },
      });
    },

    async cloneAutomation(automationId) {
      this.showLoader();
      try {
        const automationData = await axios.get(
          `automation_v2/${automationId}/show`
        );
        if (automationData && automationData.data) {
          this.setAutomationData(
            automationData.data.name,
            automationData.data.pre_condition,
            automationData.data.post_condition,
            automationData.data.status
          );
        }
        this.$root.$emit('modal-open', 'automation-clone-modal');
        this.hideLoader();
      } catch (error) {
        // show error alert
          this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async pageChanged() {
      await this.getAutomationListing();
    },

    async pageSizeChanged() {
      this.listingOptions.page = 1;
      await this.getAutomationListing();
    },

    openAutomationCreateModal() {
      this.$router.push({ name: 'AutomationCreate' });
    },

    closeAutomationNameModal() {
      this.$root.$emit('modal-close', 'automation-clone-modal');
      this.$v.model.name.$reset();
      this.setAutomationData('', [], [], 0);
    },

    async createDuplicateAutomation() {
      this.$v.model.$touch();
      if (
        !this.$v.model.name.$invalid &&
        this.model.automationTriggers &&
        this.model.automationTriggers.length &&
        this.model.automationActions &&
        this.model.automationActions.length
      ) {
        this.showLoader();
        try {
          const autoRequestBody = {
            name: this.model.name,
            pre_condition: this.model.automationTriggers,
            post_condition: this.model.automationActions,
            status: this.model.status,
          };
          await axios.post('/automation_v2', autoRequestBody);
          this.closeAutomationNameModal();
          await this.getAutomationListing();
          this.hideLoader();
        } catch (error) {
          // show error alert
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    getAutomationFilterNodeIcon(option) {
      let filterIcon = '';
      if (option.icon) {
        filterIcon = option.icon;
        if (!this.componentMapping[filterIcon]) {
          filterIcon = DefaultActivityIcon;
        }
      } else {
        filterIcon = DefaultActivityIcon;
      }
      return filterIcon;
    },

    modifyAutomationsData() {
      for (let i = 0; i < this.automations.length; i += 1) {
        this.automations[i].showDetailedView = true;
      }
    },

    toggleDetailedAutomationView(index, showView) {
      const autoItem = {
        ...this.automations[index],
        showDetailedView: !showView,
      };
      this.automations.splice(index, 1);
      this.automations.splice(index, 0, autoItem);
    },

    async sortOptionChanged(value) {
      // get the selected option from the automationSortOptions by value
      const option = this.automationSortOptions.find(
        (sortOption) => sortOption.value === value
      );

      this.listingOptions.page = 1;
      this.listingOptions.orderBy = option.orderBy;
      this.listingOptions.sortBy = option.sortBy;
      await this.getAutomationListing();
    },
  },

  components: {
    EmptyListingState,
    Pagination,
    TriggersIcon,
    ActionsIcon,
    ConditionsIcon,
    DefaultActivityIcon,
    Treeselect,
  },
};
</script>

<style lang="scss">
.automation-add-btn {
  font-size: 16px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
}

.custom-select-dropdown {
  position: relative;
  select {
    border: 1px solid #dcdcea;
    border-radius: 6px;
    height: 40px;
    background: #fff;
    padding: 0 28px 0 8px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    cursor: pointer;
  }
  &:after {
    content: '\f107';
    font-family: FontAwesome;
    font-size: 18px;
    font-weight: bold;
    color: #2d3540;
    outline: none;
    position: absolute;
    right: 12px;
    top: 7px;
    pointer-events: none;
    cursor: pointer;
  }
}
</style>

<style scoped>
.gm-automations-v2-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-automations-v2-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-automations-v2-refresh__card {
  overflow: visible;
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-automations-v2-refresh { padding: 18px 12px 32px; }
  .gm-automations-v2-refresh__workspace { padding: 16px; }
}
</style>
